/**
 * Admin Unified Ledger
 * --------------------
 * Single pane of glass for every revenue stream across the platform:
 * School POS, School Fees, Donations, Campaigns, Giving, Event POS, Bookshop,
 * Platform Subscriptions, Stripe, Auto-Billing (Housing/Dining).
 *
 * Reads from /api/finance/ledger/*
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DollarSign, TrendingUp, TrendingDown, Layers, RefreshCw, Scale,
  Loader2, Wallet, CreditCard, Banknote
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sourceLabel = {
  school_pos_sale: 'School POS',
  school_pos_wallet_topup: 'POS Wallet Top-up',
  school_pos_wallet_sale: 'POS Wallet Sale',
  school_fees_payment: 'School Fees',
  school_housing_charge: 'Boarding / Housing',
  school_dining_charge: 'Dining',
  school_library_fine: 'Library Fine',
  donation: 'Donation',
  campaign_donation: 'Campaign',
  giving: 'Online Giving',
  pledge_payment: 'Pledge Payment',
  bookshop_sale: 'Bookshop',
  subscription_payment: 'Platform Subscription',
  event_pos_sale: 'Event POS',
  event_ticket: 'Event Ticket',
  event_accommodation: 'Event Accommodation',
};

const methodIcon = (m) => {
  if (['stripe', 'card'].includes(m)) return <CreditCard className="h-3.5 w-3.5" />;
  if (m === 'wallet') return <Wallet className="h-3.5 w-3.5" />;
  return <Banknote className="h-3.5 w-3.5" />;
};

function KpiCard({ title, value, icon: Icon, tone = 'indigo', sub }) {
  const tones = {
    indigo: 'from-indigo-500/15 to-indigo-500/5 text-indigo-600 border-indigo-500/20',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-600 border-emerald-500/20',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-600 border-rose-500/20',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-600 border-amber-500/20',
  };
  return (
    <Card className={`border bg-gradient-to-br ${tones[tone]}`} data-testid={`ledger-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-white/60 ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

import { getToken } from '@/lib/authToken';

const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

export default function AdminUnifiedLedger() {
  const { token } = useAuthStore();
  const hdrs = () => ({ 'Authorization': `Bearer ${token || getToken()}`, 'Content-Type': 'application/json' });
  const [days, setDays] = useState(30);
  const [tenantId, setTenantId] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const { data: overview, isLoading: loadingOverview, refetch: refetchOverview } = useQuery({
    queryKey: ['ledger-overview', days, tenantId],
    queryFn: async () => {
      const qs = new URLSearchParams({ days: String(days) });
      if (tenantId) qs.set('tenant_id', tenantId);
      const r = await fetch(`${API_URL}/api/finance/ledger/overview?${qs}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed to load overview');
      return r.json();
    },
  });

  const { data: txPayload, isLoading: loadingTx } = useQuery({
    queryKey: ['ledger-tx', tenantId, sourceFilter],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '100' });
      if (tenantId) qs.set('tenant_id', tenantId);
      if (sourceFilter !== 'all') qs.set('source', sourceFilter);
      const r = await fetch(`${API_URL}/api/finance/ledger/transactions?${qs}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed to load transactions');
      return r.json();
    },
  });

  const { data: trial, isLoading: loadingTrial } = useQuery({
    queryKey: ['ledger-trial', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/finance/ledger/trial-balance?tenant_id=${tenantId}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed to load trial balance');
      return r.json();
    },
  });

  const sources = useMemo(() => Object.keys(sourceLabel), []);
  const totalIncome = overview?.total_income || 0;
  const totalExpense = overview?.total_expense || 0;
  const net = overview?.net || 0;
  const txCount = overview?.transaction_count || 0;

  const maxDaily = useMemo(() => {
    const d = overview?.daily_trend || [];
    return d.reduce((m, r) => Math.max(m, r.amount || 0), 0) || 1;
  }, [overview]);

  // Collapse daily trend into one series per day (income - expense)
  const dailyMerged = useMemo(() => {
    const map = new Map();
    for (const r of (overview?.daily_trend || [])) {
      const cur = map.get(r.day) || { day: r.day, income: 0, expense: 0 };
      if (r.direction === 'income') cur.income += r.amount;
      else if (r.direction === 'expense') cur.expense += r.amount;
      map.set(r.day, cur);
    }
    return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
  }, [overview]);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="unified-ledger-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
                <Scale className="h-6 w-6" />
              </span>
              Unified Finance Ledger
            </h1>
            <p className="text-slate-500 mt-1">
              Single source of truth for every revenue stream — POS, Fees, Donations, Campaigns, Giving, Bookshop, Subscriptions, Stripe.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Tenant ID (optional)"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value.trim())}
              className="w-64"
              data-testid="ledger-tenant-filter"
            />
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-36" data-testid="ledger-days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => refetchOverview()} data-testid="ledger-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {loadingOverview ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)
          ) : (
            <>
              <KpiCard title="Income" value={fmt(totalIncome)} icon={TrendingUp} tone="emerald" sub={`${txCount} transactions`} />
              <KpiCard title="Expense" value={fmt(totalExpense)} icon={TrendingDown} tone="rose" />
              <KpiCard title="Net" value={fmt(net)} icon={DollarSign} tone={net >= 0 ? 'emerald' : 'rose'} />
              <KpiCard title="Sources" value={String(overview?.by_source?.length || 0)} icon={Layers} tone="indigo" sub="revenue streams active" />
            </>
          )}
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview" data-testid="ledger-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="ledger-tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="trial-balance" data-testid="ledger-tab-trial">Trial Balance</TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-indigo-500" /> Revenue by Source</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(overview?.by_source || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No revenue recorded yet in this window.</p>
                  ) : (
                    overview.by_source.map((s) => {
                      const pct = totalIncome ? Math.round((s.amount / totalIncome) * 100) : 0;
                      return (
                        <div key={s.source} className="space-y-1" data-testid={`source-row-${s.source}`}>
                          <div className="flex justify-between text-sm">
                            <span className="font-medium text-slate-700">{sourceLabel[s.source] || s.source}</span>
                            <span className="text-slate-900">{fmt(s.amount)} <span className="text-xs text-slate-400">· {s.count} txn</span></span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-emerald-500" /> By Method</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(overview?.by_method || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No data.</p>
                  ) : (
                    overview.by_method.map((m) => (
                      <div key={m.method || 'unknown'} className="flex items-center justify-between text-sm py-1.5 border-b last:border-b-0">
                        <span className="flex items-center gap-2 text-slate-700">
                          {methodIcon(m.method)} {m.method || 'unknown'}
                        </span>
                        <span className="font-medium">{fmt(m.amount)}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-indigo-500" /> Daily Trend (14 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {dailyMerged.length === 0 ? (
                  <p className="text-sm text-slate-500">No data in the last 14 days.</p>
                ) : (
                  <div className="flex items-end gap-1 h-40">
                    {dailyMerged.map((d) => {
                      const ih = Math.max(2, Math.round((d.income / maxDaily) * 100));
                      const eh = Math.max(0, Math.round((d.expense / maxDaily) * 100));
                      return (
                        <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} · +${fmt(d.income)} -${fmt(d.expense)}`}>
                          <div className="w-full flex flex-col-reverse gap-0.5 h-32">
                            <div className="bg-emerald-500 rounded-t" style={{ height: `${ih}%` }} />
                            {eh > 0 && <div className="bg-rose-400 rounded-t" style={{ height: `${eh}%` }} />}
                          </div>
                          <span className="text-[10px] text-slate-500">{d.day.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TRANSACTIONS */}
          <TabsContent value="transactions" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle>Recent Transactions</CardTitle>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-56" data-testid="ledger-source-filter">
                    <SelectValue placeholder="All sources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    {sources.map((s) => (
                      <SelectItem key={s} value={s}>{sourceLabel[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent>
                {loadingTx ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Tenant</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(txPayload?.items || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500 py-10">No transactions recorded yet.</TableCell>
                        </TableRow>
                      ) : (
                        txPayload.items.map((t) => (
                          <TableRow key={t._id} data-testid={`ledger-tx-${t._id}`}>
                            <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                              {t.timestamp ? new Date(t.timestamp).toLocaleString() : ''}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{sourceLabel[t.source] || t.source}</Badge>
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate" title={t.category}>{t.category || '—'}</TableCell>
                            <TableCell className="text-xs uppercase text-slate-600">{t.method}</TableCell>
                            <TableCell className="text-xs font-mono text-slate-500">{(t.tenant_id || '').slice(0, 10)}</TableCell>
                            <TableCell className={`text-right font-semibold ${t.direction === 'expense' ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {t.direction === 'expense' ? '-' : '+'}{fmt(t.amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
                <p className="text-xs text-slate-400 mt-3">
                  Showing {txPayload?.items?.length || 0} of {txPayload?.total || 0} total.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TRIAL BALANCE */}
          <TabsContent value="trial-balance" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-indigo-500" />
                  Trial Balance
                  {trial && (
                    <Badge className={trial.balanced ? 'bg-emerald-500' : 'bg-rose-500'}>
                      {trial.balanced ? 'Balanced' : 'Out of balance'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!tenantId ? (
                  <p className="text-sm text-slate-500">Enter a tenant ID above to view trial balance per tenant (double-entry view).</p>
                ) : loadingTrial ? (
                  <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(trial?.accounts || []).map((a) => (
                        <TableRow key={a.code}>
                          <TableCell className="font-mono text-xs">{a.code}</TableCell>
                          <TableCell>{a.name}</TableCell>
                          <TableCell className="text-xs uppercase text-slate-500">{a.type}</TableCell>
                          <TableCell className="text-right">{a.debit ? fmt(a.debit) : ''}</TableCell>
                          <TableCell className="text-right">{a.credit ? fmt(a.credit) : ''}</TableCell>
                          <TableCell className={`text-right font-medium ${a.balance > 0 ? 'text-emerald-600' : a.balance < 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                            {a.balance !== 0 ? fmt(Math.abs(a.balance)) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {trial && (
                        <TableRow className="font-bold border-t-2">
                          <TableCell colSpan={3}>Totals</TableCell>
                          <TableCell className="text-right">{fmt(trial.total_debit)}</TableCell>
                          <TableCell className="text-right">{fmt(trial.total_credit)}</TableCell>
                          <TableCell />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
